import React, { useState } from 'react';
import { Modal, Box, Typography, Button, Snackbar, Alert, CircularProgress, Chip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { grey, orange, green } from '@mui/material/colors';
import BASE_URL from '../../config';

const style = {
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 }, bgcolor: 'white', borderRadius: 4, p: 4, outline: 'none',
};

export default function PopUnlockUser({ user, refresh }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isLocked = user.is_locked;

  const handleUnlock = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/users/${user.id}/unlock`, {
        method: 'POST',
      });

      if (res.ok) {
        setSnackbar({ open: true, message: 'Usuario desbloqueado correctamente', severity: 'success' });
        setTimeout(() => {
          setOpen(false);
          refresh();
        }, 1200);
      } else {
        const data = await res.json();
        setSnackbar({ open: true, message: data.message || 'Error al desbloquear', severity: 'error' });
        setLoading(false);
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Error de conexión', severity: 'error' });
      setLoading(false);
    }
  };

  return (
    <>
      {isLocked ? (
        <Chip
          icon={<LockOutlinedIcon />}
          label={`Bloqueado: ${user.lock_reason || 'Razón desconocida'}`}
          size="small"
          color="error"
          variant="outlined"
          onClick={() => setOpen(true)}
          sx={{ cursor: 'pointer' }}
        />
      ) : (
        <Chip
          icon={<LockOpenOutlinedIcon />}
          label="Desbloqueado"
          size="small"
          variant="filled"
          sx={{ bgcolor: green[500], color: 'white' }}
        />
      )}

      <Modal open={open} onClose={() => !loading && setOpen(false)}>
        <Box sx={style}>
          <Typography variant="h6" mb={2}>🔒 Usuario Bloqueado</Typography>

          <Box sx={{ bgcolor: grey[50], p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Usuario:</strong> {user.nombre_completo}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Email:</strong> {user.email}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Intentos fallidos:</strong> {user.failed_login_attempts || 0}
            </Typography>
            {user.lock_reason && (
              <Typography variant="body2" color="textSecondary">
                <strong>Razón:</strong> {user.lock_reason}
              </Typography>
            )}
            {user.account_locked_until && (
              <Typography variant="body2" color="textSecondary" mt={1}>
                <strong>Bloqueado hasta:</strong> {new Date(user.account_locked_until).toLocaleString()}
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color="warning.main" mb={2}>
            ⚠️ Este usuario no puede iniciar sesión. Haz clic en "Desbloquear" para restaurar el acceso.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={loading}
              sx={{
                color: 'black',
                bgcolor: grey[100],
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': { bgcolor: grey[200] }
              }}
              onClick={() => setOpen(false)}
            >
              Cerrar
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: orange[500],
                color: 'white',
                '&:hover': { bgcolor: orange[700] },
                textTransform: 'none',
                borderRadius: '8px'
              }}
              onClick={handleUnlock}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : '🔓 Desbloquear'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
